import * as ft from 'funtypes'
import { validate, validateRows } from '../validation.js'
import {
  EIPA_FILE_CLASS,
  EIPA_FILES,
  EipaDictionaryFile,
  EipaDynamic,
  type EipaDynamicFile,
  type EipaFileClass,
  type EipaFileName,
  EipaOperator,
  type EipaOperatorFile,
  EipaPoint,
  type EipaPointFile,
  EipaPool,
  type EipaPoolFile,
  EipaRowsEnvelope,
  type EipaSnapshot,
  EipaStation,
  type EipaStationFile,
  toEipaDictionaryEnvelope,
} from './types.js'

/**
 * EIPA reader client.
 *
 * Access to the JSON files is granted after registration with UDT and is rate
 * limited per hour, so this implementation is deliberately conservative:
 *
 * - every request passes a token bucket ({@link RequestBudget}) sized from the
 *   job's slice of the hourly limit for that file's *class*
 *   ({@link EIPA_REQUEST_ALLOCATION}), memoised per job per process, so the
 *   nightly ingest and the 10-minute status job cannot together spend more than
 *   the account is allowed;
 * - {@link EipaClient.fetchAll} walks the six files one at a time - never six
 *   concurrent downloads of a rate-limited account;
 * - conditional requests (`If-None-Match` / `If-Modified-Since`) are sent for
 *   every file the caller has already seen, so an unchanged file costs a 304
 *   instead of half a megabyte;
 * - 429 and 5xx are retried with exponential backoff plus jitter, honouring
 *   `Retry-After` when the server sends one, and fail with a terminal error that
 *   says what was tried;
 * - every response is validated through the runtypes in `./types.ts` before it
 *   reaches the pipeline, row by row, so a failure names the field path and the
 *   row's own id.
 *
 * Auth is a single opaque token that UDT mails when the reader account is
 * confirmed, and it travels as the last path segment of the URL rather than in
 * a header - see {@link eipaPathTokenAuth}. It still goes through the
 * {@link EipaAuthStrategy} seam, so a change of scheme stays a one-line change
 * at the call site. See `docs/deploy-notes-eipa.md`.
 */

export interface EipaCredentials {
  /**
   * The reader token from UDT's access mail - a 32-character hex string that is
   * the whole of the credential. There is no username and no password: the
   * token alone identifies the account, which is why it must be treated as a
   * secret and never logged, even though it rides in a URL.
   */
  readonly token: string
}

/** What to do when a row inside an otherwise well-formed file does not validate. */
export type EipaRowErrorMode = 'throw' | 'collect'

export interface EipaClientOptions {
  /**
   * Base URL the six files hang off, e.g.
   * `https://eipa.udt.gov.pl/reader/export-data`. The client appends
   * `/<file>/<token>`; there is no `.json` suffix on the wire.
   */
  readonly baseUrl: string
  /** Issued by UDT after registration. */
  readonly credentials: EipaCredentials
  /** Injected for testability; defaults to the global `fetch` when omitted. */
  readonly fetch?: typeof globalThis.fetch

  // Everything below is optional and has a working default. The six fetch
  // methods above are the frozen contract; these are the knobs the ingest and
  // status jobs need to stay inside one account's hourly allowance.

  /**
   * Which scheduled job's allowance this client spends. Default `dailyIngest`;
   * the status refresh passes `statusRefresh`. Ignored when {@link budget} is
   * given.
   */
  readonly job?: EipaJob
  /**
   * Overrides the job's share of {@link EIPA_REQUEST_ALLOCATION}, per class.
   * Only read by the first caller in the process, since the per-job buckets are
   * memoised.
   */
  readonly requestsPerHour?: Partial<Record<EipaFileClass, number>>
  /**
   * The token buckets to spend from, one per rate-limited class. Defaults to
   * `createRequestBudgets(job)` - one pair per job per process, so two clients
   * built in the same invocation cannot each spend the whole allowance.
   */
  readonly budget?: EipaBudgets
  readonly retry?: EipaRetryOptions
  /**
   * Where ETag / Last-Modified (and optionally the body) are remembered between
   * calls. Defaults to an in-memory store, which is per function instance; pass
   * a persistent one to make conditional requests survive a cold start.
   */
  readonly conditionalStore?: EipaConditionalStore
  /** The whole auth surface. Defaults to {@link eipaBasicAuth}. */
  readonly auth?: EipaAuthStrategy
  /** Sent on every request so UDT can identify the client. */
  readonly userAgent?: string
  /** Per-request timeout. Default 60s. */
  readonly requestTimeoutMs?: number
  /**
   * `throw` (default) rejects the whole file when any row is malformed;
   * `collect` keeps the valid rows and reports the rest through
   * {@link EipaRawFile.rowErrors}, which is what the raw snapshot job wants -
   * one bad row should not stop the day's download from being archived.
   */
  readonly rowErrors?: EipaRowErrorMode
  /** Injected for testability. */
  readonly sleep?: (ms: number) => Promise<void>
  /** Injected for testability; must return [0, 1). */
  readonly random?: () => number
  /** Injected for testability. */
  readonly now?: () => number
}

export interface EipaClient {
  fetchDictionary(): Promise<EipaDictionaryFile>
  fetchOperators(): Promise<EipaOperatorFile>
  fetchPools(): Promise<EipaPoolFile>
  fetchStations(): Promise<EipaStationFile>
  fetchPoints(): Promise<EipaPointFile>
  fetchDynamic(): Promise<EipaDynamicFile>
  /** All six files for one pipeline run. */
  fetchAll(): Promise<EipaSnapshot>
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/** Base class for everything this client throws. */
export class EipaError extends Error {
  /** Which of the six files was being fetched, when known. */
  readonly file?: EipaFileName
  readonly url?: string

  constructor(
    message: string,
    context: { file?: EipaFileName; url?: string; cause?: unknown } = {},
  ) {
    super(message, context.cause === undefined ? undefined : { cause: context.cause })
    this.name = 'EipaError'
    this.file = context.file
    this.url = context.url
  }
}

/**
 * The reader credentials are missing or blank. Thrown from
 * {@link createEipaClient} rather than from the first request, so a
 * misconfigured deploy fails on the first line of the run with a message that
 * says how to fix it.
 */
export class EipaCredentialsError extends EipaError {
  constructor(message: string) {
    super(message)
    this.name = 'EipaCredentialsError'
  }
}

/** A response the client will not retry: 4xx other than 429. */
export class EipaHttpError extends EipaError {
  readonly status: number
  /** First 300 characters of the body, to make a gateway error page readable. */
  readonly bodySnippet: string

  constructor(
    message: string,
    context: { file: EipaFileName; url: string; status: number; bodySnippet: string },
  ) {
    super(message, { file: context.file, url: context.url })
    this.name = 'EipaHttpError'
    this.status = context.status
    this.bodySnippet = context.bodySnippet
  }
}

/** Retries were exhausted. Terminal: the run should stop and be retried later. */
export class EipaRetryLimitError extends EipaError {
  readonly attempts: number
  readonly lastStatus?: number

  constructor(
    message: string,
    context: {
      file: EipaFileName
      url: string
      attempts: number
      lastStatus?: number
      cause?: unknown
    },
  ) {
    super(message, { file: context.file, url: context.url, cause: context.cause })
    this.name = 'EipaRetryLimitError'
    this.attempts = context.attempts
    this.lastStatus = context.lastStatus
  }
}

/**
 * The server asked us to wait longer than this client is allowed to sleep.
 * Sitting on a `Retry-After: 1800` inside a 540s function would only burn the
 * timeout, so it is reported instead - the next scheduled run will pick it up.
 */
export class EipaRateLimitError extends EipaError {
  readonly retryAfterMs: number

  constructor(message: string, context: { file: EipaFileName; url: string; retryAfterMs: number }) {
    super(message, { file: context.file, url: context.url })
    this.name = 'EipaRateLimitError'
    this.retryAfterMs = context.retryAfterMs
  }
}

/** The local hourly budget is spent and would not refill in time. */
export class EipaBudgetExhaustedError extends EipaError {
  readonly limitPerHour: number
  readonly waitMs: number

  constructor(message: string, context: { limitPerHour: number; waitMs: number }) {
    super(message)
    this.name = 'EipaBudgetExhaustedError'
    this.limitPerHour = context.limitPerHour
    this.waitMs = context.waitMs
  }
}

/**
 * A response that did not match its runtype. `issues` holds one formatted
 * message per offending row, e.g.
 * `pool.json row 4211: latitude: Expected number, but was string`.
 */
export class EipaValidationError extends EipaError {
  readonly issues: readonly string[]

  constructor(file: EipaFileName, issues: readonly string[]) {
    const [first = 'unknown failure'] = issues
    const extra = issues.length > 1 ? ` (+${issues.length - 1} more)` : ''
    super(`${EIPA_FILES[file]} failed validation: ${first}${extra}`, { file })
    this.name = 'EipaValidationError'
    this.issues = issues
  }
}

// ---------------------------------------------------------------------------
// Auth - the one place the scheme lives
// ---------------------------------------------------------------------------

export interface EipaAuthRequest {
  readonly url: string
  readonly headers: Headers
  readonly credentials: EipaCredentials
}

/**
 * Turns an unauthenticated request into an authenticated one.
 *
 * The client is written against this seam and nothing else, so if UDT ever
 * moves the token into a header it is one line here and one at the call site.
 */
export type EipaAuthStrategy = (request: EipaAuthRequest) => EipaAuthRequest

/**
 * `<base>/<file>/<token>` - the scheme UDT actually uses.
 *
 * The token is the last path segment, so there is no `Authorization` header and
 * no query string: the URL *is* the credential. Two consequences the rest of
 * the client is built around:
 *
 * - a URL must never be logged or put in an error message verbatim, because it
 *   carries the secret. {@link redactEipaUrl} is what everything user-facing
 *   goes through;
 * - a 401/403 cannot be distinguished from a typo in the path, so the hint on
 *   an unauthorised response names both.
 */
export const eipaPathTokenAuth: EipaAuthStrategy = (request) => {
  const url = new URL(request.url)
  url.pathname = `${url.pathname.replace(/\/+$/, '')}/${encodeURIComponent(
    request.credentials.token,
  )}`
  return { ...request, url: url.toString() }
}

/**
 * A URL with the token path segment replaced by `<token>`.
 *
 * Applied to everything that leaves the client - error messages, log fields -
 * so a stack trace in Cloud Logging cannot hand over read access to the
 * account. The token is the final segment under `export-data/<file>/`, and only
 * a segment that looks like one is removed, so a URL without a token survives
 * unchanged and stays readable.
 */
export const redactEipaUrl = (url: string): string =>
  url.replace(/\/[0-9a-f]{16,}(?=\/?$)/i, '/<token>')

// ---------------------------------------------------------------------------
// Request budget
// ---------------------------------------------------------------------------

const HOUR_MS = 60 * 60 * 1000

/**
 * UDT's published hourly download limits, per file class.
 *
 * These are the real figures from the access mail, and they are not one cap but
 * two very different ones: the five static files share ten downloads an hour
 * between them, while `dynamic` gets 240. Exceeding either suspends access
 * until the next hour.
 *
 * The static figure is read here as an *aggregate* across the five files rather
 * than ten downloads of each. The mail says only "pliki z danymi statycznymi -
 * 10 pobran na godzine", which is ambiguous; the aggregate reading is the
 * conservative one and is correct under either.
 */
export const EIPA_HOURLY_LIMIT = {
  static: 10,
  dynamic: 240,
} as const satisfies Record<EipaFileClass, number>

/**
 * Static split of each class limit across the two scheduled jobs. Must sum, per
 * class, to at most {@link EIPA_HOURLY_LIMIT} - {@link eipaAllocationFits}
 * reports whether it still does, and a test holds it.
 *
 * It is a *static* split rather than shared accounting because a token bucket
 * lives in one Node process: two Cloud Function instances of the same job would
 * hold two buckets and spend twice. Giving each job its own slice makes the
 * arithmetic hold whatever runs when, provided each scheduled function is capped
 * at `maxInstances: 1` (both are). Cross-instance coordination - a Firestore
 * counter - is deliberately out of scope; swap in a {@link RequestBudget}
 * implementation if it is ever needed.
 */
export const EIPA_REQUEST_ALLOCATION = {
  /**
   * 5 static files once a day, plus 3 spare for the client's own retries. This
   * is the tight one: the whole nightly download has to fit in 10, and a forced
   * run in the same hour spends its own 5 on top (see docs/deploy-notes-eipa.md).
   */
  dailyIngest: { static: 8, dynamic: 12 },
  /**
   * No static files at all - the status refresh reads `dynamic` and takes site
   * membership from the last publish. A zero share is deliberate: it makes a
   * future static fetch from this job fail loudly rather than quietly eat the
   * ingest's allowance.
   */
  statusRefresh: { static: 0, dynamic: 60 },
} as const satisfies Record<string, Record<EipaFileClass, number>>

export type EipaJob = keyof typeof EIPA_REQUEST_ALLOCATION

/** The classes whose split does not fit inside the published limit; empty is good. */
export const eipaAllocationOverruns = (): EipaFileClass[] =>
  (Object.keys(EIPA_HOURLY_LIMIT) as EipaFileClass[]).filter((fileClass) => {
    const allocated = Object.values(EIPA_REQUEST_ALLOCATION).reduce(
      (sum, share) => sum + share[fileClass],
      0,
    )
    return allocated > EIPA_HOURLY_LIMIT[fileClass]
  })

/** True while both class splits still fit inside their published limits. */
export const eipaAllocationFits = (): boolean => eipaAllocationOverruns().length === 0

/** How long {@link RequestBudget.acquire} may block before giving up. */
export const DEFAULT_BUDGET_WAIT_MS = 2 * 60 * 1000

/** The frozen budget seam. Both scheduled jobs spend through one of these. */
export interface RequestBudget {
  /** Consume one request, waiting for the bucket to refill if it is empty. */
  acquire(): Promise<void>
  /** Consume one request if one is free right now. Never waits. */
  tryAcquire(): boolean
  /** Whole requests available right now, refill applied. */
  remaining(): number
}

/** A {@link RequestBudget} that knows what it was configured with. */
export interface EipaRequestBudget extends RequestBudget {
  readonly limitPerHour: number
}

export interface EipaRequestBudgetOptions {
  readonly limitPerHour?: number
  /** Bucket capacity, i.e. the largest burst allowed. Defaults to `limitPerHour`. */
  readonly burst?: number
  /** Longest an `acquire` may wait for a refill. Default 2 minutes. */
  readonly maxWaitMs?: number
  readonly now?: () => number
  readonly sleep?: (ms: number) => Promise<void>
}

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

/**
 * A token bucket over the hourly account limit.
 *
 * Tokens refill continuously at `limitPerHour / hour` rather than resetting on
 * the hour, because we do not know where UDT's window starts. Acquisitions are
 * serialised, so two callers can never both take the last token.
 */
export const createEipaRequestBudget = (
  options: EipaRequestBudgetOptions = {},
): EipaRequestBudget => {
  const limitPerHour = options.limitPerHour ?? EIPA_REQUEST_ALLOCATION.dailyIngest.static
  const capacity = options.burst ?? limitPerHour
  const maxWaitMs = options.maxWaitMs ?? DEFAULT_BUDGET_WAIT_MS
  const now = options.now ?? Date.now
  const sleep = options.sleep ?? defaultSleep
  const refillPerMs = limitPerHour / HOUR_MS

  let tokens = capacity
  let updatedAt = now()
  let queue: Promise<void> = Promise.resolve()

  const refill = (): void => {
    const at = now()
    tokens = Math.min(capacity, tokens + Math.max(0, at - updatedAt) * refillPerMs)
    updatedAt = at
  }

  const take = async (): Promise<void> => {
    const deadline = now() + maxWaitMs
    while (true) {
      refill()
      if (tokens >= 1) {
        tokens -= 1
        return
      }
      const waitMs = Math.ceil((1 - tokens) / refillPerMs)
      if (now() + waitMs > deadline)
        throw new EipaBudgetExhaustedError(
          limitPerHour === 0
            ? 'EIPA request budget is zero: this job is not allocated any requests of this ' +
                "file's class in EIPA_REQUEST_ALLOCATION, so the fetch was refused rather than " +
                "spent from another job's share."
            : `EIPA request budget spent: ${limitPerHour} requests/hour would need another ` +
                `${Math.round(waitMs / 1000)}s to refill, which is over the ${Math.round(
                  maxWaitMs / 1000,
                )}s this run may wait. Raise the job's share in EIPA_REQUEST_ALLOCATION only if ` +
                'UDT allows it.',
          { limitPerHour, waitMs },
        )
      await sleep(Math.max(1, Math.min(waitMs, deadline - now())))
    }
  }

  return {
    limitPerHour,
    remaining: () => {
      refill()
      return Math.floor(tokens)
    },
    tryAcquire: () => {
      refill()
      if (tokens < 1) return false
      tokens -= 1
      return true
    },
    acquire: () => {
      const next = queue.then(take)
      queue = next.then(
        () => undefined,
        () => undefined,
      )
      return next
    },
  }
}

/** One bucket per rate-limited class. A client spends from whichever the file belongs to. */
export interface EipaBudgets {
  readonly static: EipaRequestBudget
  readonly dynamic: EipaRequestBudget
}

const jobBudgets = new Map<EipaJob, EipaBudgets>()

/**
 * The budgets a scheduled job spends from:
 * `createRequestBudgets('dailyIngest')` for the nightly ingest,
 * `createRequestBudgets('statusRefresh')` for the 10-minute status refresh.
 *
 * One pair of buckets per job per process, memoised: two clients built inside
 * the same invocation share them, which is what stops a job from spending its
 * allowance twice over. The split between jobs is static - see
 * {@link EIPA_REQUEST_ALLOCATION} for why a per-process bucket cannot do real
 * shared accounting.
 */
export const createRequestBudgets = (
  job: EipaJob,
  options: Partial<Record<EipaFileClass, EipaRequestBudgetOptions>> = {},
): EipaBudgets => {
  const existing = jobBudgets.get(job)
  if (existing !== undefined) return existing
  const forClass = (fileClass: EipaFileClass): EipaRequestBudget =>
    createEipaRequestBudget({
      ...options[fileClass],
      limitPerHour: options[fileClass]?.limitPerHour ?? EIPA_REQUEST_ALLOCATION[job][fileClass],
    })
  const budgets: EipaBudgets = { static: forClass('static'), dynamic: forClass('dynamic') }
  jobBudgets.set(job, budgets)
  return budgets
}

/** Drops the memoised per-job budgets. Tests only. */
export const resetRequestBudgets = (): void => {
  jobBudgets.clear()
}

// ---------------------------------------------------------------------------
// Conditional requests
// ---------------------------------------------------------------------------

export interface EipaCacheEntry {
  readonly etag?: string
  readonly lastModified?: string
  /**
   * The body that produced these validators. A store that keeps it can answer a
   * 304 without a second request; one that only keeps validators (the raw
   * snapshot job seeds them from the previous manifest) makes the client
   * re-request when a caller actually needs the data.
   */
  readonly body?: string
}

export interface EipaConditionalStore {
  get(file: EipaFileName): Promise<EipaCacheEntry | undefined> | EipaCacheEntry | undefined
  set(file: EipaFileName, entry: EipaCacheEntry): Promise<void> | void
}

/** Per-instance store. Survives warm invocations, not a cold start. */
export const createMemoryConditionalStore = (): EipaConditionalStore => {
  const entries = new Map<EipaFileName, EipaCacheEntry>()
  return {
    get: (file) => entries.get(file),
    set: (file, entry) => {
      entries.set(file, entry)
    },
  }
}

/** Validators without bodies, e.g. seeded from a previous run's manifest. */
export const createValidatorConditionalStore = (
  seed: Partial<Record<EipaFileName, EipaCacheEntry>>,
): EipaConditionalStore => {
  const entries = new Map<EipaFileName, EipaCacheEntry>()
  Object.entries(seed).forEach(([file, entry]) => {
    if (entry !== undefined) entries.set(file as EipaFileName, entry)
  })
  return {
    get: (file) => entries.get(file),
    set: (file, entry) => {
      entries.set(file, { etag: entry.etag, lastModified: entry.lastModified })
    },
  }
}

// ---------------------------------------------------------------------------
// Retry
// ---------------------------------------------------------------------------

export interface EipaRetryOptions {
  /** Total attempts per file, first try included. Default 4. */
  readonly maxAttempts?: number
  /** Delay before the second attempt. Default 1s, doubling from there. */
  readonly baseDelayMs?: number
  /** Ceiling for one backoff delay, and for an honoured `Retry-After`. Default 30s. */
  readonly maxDelayMs?: number
}

const DEFAULT_RETRY: Required<EipaRetryOptions> = {
  maxAttempts: 4,
  baseDelayMs: 1_000,
  maxDelayMs: 30_000,
}

/**
 * Equal jitter: half the exponential delay, plus a random half.
 *
 * Keeping a floor matters more than perfect spread here - two functions hammering
 * a rate-limited account in lockstep is exactly what the jitter is for, but a
 * delay that can round to zero would defeat the backoff.
 */
export const eipaBackoffDelayMs = (
  attempt: number,
  retry: Required<EipaRetryOptions>,
  random: () => number,
): number => {
  const exponential = Math.min(retry.maxDelayMs, retry.baseDelayMs * 2 ** (attempt - 1))
  return Math.round(exponential / 2 + random() * (exponential / 2))
}

/** `Retry-After` in milliseconds, from either a delta-seconds or an HTTP-date. */
export const parseRetryAfter = (header: string | null, now: number): number | undefined => {
  if (header === null) return undefined
  const trimmed = header.trim()
  if (trimmed === '') return undefined
  const seconds = Number(trimmed)
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000)
  const date = Date.parse(trimmed)
  if (Number.isNaN(date)) return undefined
  return Math.max(0, date - now)
}

const isRetryableStatus = (status: number): boolean => status === 429 || status >= 500

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

/**
 * Loose envelope, so a row-level failure can name the row instead of the array.
 *
 * `EipaRowsEnvelope` is the one definition of the `{ data, generated }` wrapper;
 * instantiating it with `ft.Unknown` is how this file gets a row-agnostic
 * version without restating the shape.
 */
const RowEnvelope = EipaRowsEnvelope(ft.Unknown)

interface ParsedFile<T> {
  readonly value: T
  /** Undefined for `dictionary` alone, which UDT publishes without an envelope. */
  readonly generated: string | undefined
  /** Rows in the payload, malformed ones included. */
  readonly rowCount: number
  readonly rowErrors: readonly string[]
}

const rowIdBy =
  (key: string) =>
  (row: unknown, index: number): string | number => {
    if (typeof row !== 'object' || row === null) return index
    const value = (row as Record<string, unknown>)[key]
    if (typeof value === 'number' || typeof value === 'string') return value
    return index
  }

const parseRowFile = <T>(
  file: EipaFileName,
  row: ft.RuntypeBase<T>,
  rowId: (row: unknown, index: number) => string | number,
  payload: unknown,
  mode: EipaRowErrorMode,
): ParsedFile<{ data: T[]; generated: string }> => {
  const source = EIPA_FILES[file]
  const envelope = validate(RowEnvelope, payload, { source })
  if (!envelope.ok) throw new EipaValidationError(file, [envelope.error])

  const parsed = validateRows(row, envelope.value.data, { source, rowId })
  if (parsed.errors.length > 0 && mode === 'throw')
    throw new EipaValidationError(file, parsed.errors)

  return {
    value: { data: parsed.rows, generated: envelope.value.generated },
    generated: envelope.value.generated,
    rowCount: envelope.value.data.length,
    rowErrors: parsed.errors,
  }
}

const parseDictionaryFile = (payload: unknown): ParsedFile<EipaDictionaryFile> => {
  const source = EIPA_FILES.dictionary
  // The body is the bare table object; wrap it so the pipeline sees the same
  // `{ data, generated }` shape it gets from the other five files.
  const result = validate(EipaDictionaryFile, toEipaDictionaryEnvelope(payload), { source })
  if (!result.ok) throw new EipaValidationError('dictionary', [result.error])
  const rowCount = Object.values(result.value.data).reduce((sum, table) => sum + table.length, 0)
  return {
    value: result.value,
    generated: result.value.generated,
    rowCount,
    rowErrors: [],
  }
}

// ---------------------------------------------------------------------------
// Raw access
// ---------------------------------------------------------------------------

/** A file whose body came back, validated but still in its original bytes. */
export interface EipaRawBody {
  readonly file: EipaFileName
  readonly url: string
  readonly notModified: false
  /** Exactly what the server sent. Hash and archive this, not a re-serialisation. */
  readonly text: string
  /** `generated` out of the envelope; undefined for `dictionary`, which has none. */
  readonly generated: string | undefined
  /** Rows in the payload, malformed ones included. */
  readonly rowCount: number
  /** Non-empty only under `rowErrors: 'collect'`. */
  readonly rowErrors: readonly string[]
  readonly etag?: string
  readonly lastModified?: string
}

/** The server answered 304 and the store held no body to fall back on. */
export interface EipaRawNotModified {
  readonly file: EipaFileName
  readonly url: string
  readonly notModified: true
  readonly etag?: string
  readonly lastModified?: string
}

export type EipaRawFile = EipaRawBody | EipaRawNotModified

/**
 * The client plus raw access, for jobs that archive the bytes rather than the
 * parsed model. {@link EipaClient} stays exactly as frozen; this is a superset.
 */
export interface EipaRawClient extends EipaClient {
  /**
   * One file, validated, with its original body. Returns
   * {@link EipaRawNotModified} when the server says 304 and the conditional
   * store had no body - the caller already has these bytes.
   */
  fetchRawFile(file: EipaFileName): Promise<EipaRawFile>
  /** The budgets this client spends from, for logging what a run cost. */
  readonly budgets: EipaBudgets
}

// ---------------------------------------------------------------------------
// The client
// ---------------------------------------------------------------------------

interface HttpResult {
  readonly status: number
  readonly text?: string
  readonly etag?: string
  readonly lastModified?: string
}

const headerOrUndefined = (headers: Headers, name: string): string | undefined =>
  headers.get(name) ?? undefined

const assertCredentials = (credentials: EipaCredentials): void => {
  if (credentials.token.trim() !== '') return
  throw new EipaCredentialsError(
    'Missing EIPA reader token: EIPA_TOKEN is empty. The reader account is registered at ' +
      'https://eipa.udt.gov.pl/reader/register and UDT mails the token as the last path ' +
      'segment of each export-data URL; it is stored in Secret Manager - see ' +
      'docs/deploy-notes-eipa.md for the exact `firebase functions:secrets:set` command. ' +
      'For the emulator, put it in functions/.secret.local.',
  )
}

export const createEipaClient = (options: EipaClientOptions): EipaClient =>
  createEipaRawClient(options)

export const createEipaRawClient = (options: EipaClientOptions): EipaRawClient => {
  assertCredentials(options.credentials)

  const doFetch = options.fetch ?? globalThis.fetch
  const auth = options.auth ?? eipaPathTokenAuth
  const budgets =
    options.budget ??
    createRequestBudgets(options.job ?? 'dailyIngest', {
      static: { limitPerHour: options.requestsPerHour?.static },
      dynamic: { limitPerHour: options.requestsPerHour?.dynamic },
    })
  /** The bucket a file's class draws on. `dynamic` is 24x the static allowance. */
  const budgetFor = (file: EipaFileName): RequestBudget => budgets[EIPA_FILE_CLASS[file]]
  const store = options.conditionalStore ?? createMemoryConditionalStore()
  const retry = { ...DEFAULT_RETRY, ...options.retry }
  const rowErrorMode = options.rowErrors ?? 'throw'
  const userAgent = options.userAgent ?? 'ev-charging-map/0.1 (+https://ev.dziardziel.com)'
  const requestTimeoutMs = options.requestTimeoutMs ?? 60_000
  const sleep = options.sleep ?? defaultSleep
  const random = options.random ?? Math.random
  const now = options.now ?? Date.now

  const base = options.baseUrl.endsWith('/') ? options.baseUrl : `${options.baseUrl}/`
  /**
   * `<base>/<file>` - the token is appended by {@link eipaPathTokenAuth}, not
   * here, so this URL is the one safe to log and to put in an error. The path
   * segment is the file *key* (`pool`), not `EIPA_FILES[file]` (`pool.json`):
   * the reader URLs carry no suffix, while the archive object names still do.
   */
  const urlFor = (file: EipaFileName): string => new URL(file, base).toString()

  /** One attempt, budget included. Never retries; that is the caller's job. */
  const attemptRequest = async (
    file: EipaFileName,
    url: string,
    conditional: boolean,
  ): Promise<HttpResult> => {
    const cached = conditional ? await store.get(file) : undefined
    const headers = new Headers({ Accept: 'application/json', 'User-Agent': userAgent })
    if (cached?.etag !== undefined) headers.set('If-None-Match', cached.etag)
    if (cached?.lastModified !== undefined) headers.set('If-Modified-Since', cached.lastModified)

    const request = auth({ url, headers, credentials: options.credentials })
    await budgetFor(file).acquire()

    const response = await doFetch(request.url, {
      method: 'GET',
      headers: request.headers,
      redirect: 'follow',
      signal: AbortSignal.timeout(requestTimeoutMs),
    })

    const etag = headerOrUndefined(response.headers, 'etag')
    const lastModified = headerOrUndefined(response.headers, 'last-modified')

    if (response.status === 304)
      return {
        status: 304,
        etag: etag ?? cached?.etag,
        lastModified: lastModified ?? cached?.lastModified,
        text: cached?.body,
      }

    if (response.ok) {
      const text = await response.text()
      await store.set(file, { etag, lastModified, body: text })
      return { status: response.status, text, etag, lastModified }
    }

    if (isRetryableStatus(response.status)) {
      const retryAfterMs = parseRetryAfter(response.headers.get('retry-after'), now())
      if (retryAfterMs !== undefined && retryAfterMs > retry.maxDelayMs)
        throw new EipaRateLimitError(
          `${EIPA_FILES[file]}: EIPA answered ${response.status} and asked to wait ` +
            `${Math.round(retryAfterMs / 1000)}s, longer than this run may sleep. The hourly ` +
            'account limit is likely spent; the next scheduled run will pick it up.',
          { file, url, retryAfterMs },
        )
      throw new RetryableResponse(response.status, retryAfterMs)
    }

    const bodySnippet = (await response.text().catch(() => '')).slice(0, 300)
    const hint =
      response.status === 401 || response.status === 403
        ? ' The reader token was rejected - check EIPA_TOKEN in Secret Manager, and that the ' +
          'account has not been deleted for 30 days of inactivity (see docs/deploy-notes-eipa.md).'
        : response.status === 404
          ? ' The token is the last path segment, so a 404 is as likely a wrong or revoked ' +
            'EIPA_TOKEN as a wrong EIPA_BASE_URL. Check both against the UDT access mail and ' +
            'https://eipa.udt.gov.pl/reader/docs.'
          : ''
    throw new EipaHttpError(
      `${EIPA_FILES[file]}: EIPA answered ${response.status} ${response.statusText}.${hint}`,
      { file, url, status: response.status, bodySnippet },
    )
  }

  const request = async (
    file: EipaFileName,
    url: string,
    conditional: boolean,
  ): Promise<HttpResult> => {
    let lastStatus: number | undefined
    let lastCause: unknown

    for (let attempt = 1; attempt <= retry.maxAttempts; attempt += 1) {
      try {
        return await attemptRequest(file, url, conditional)
      } catch (error) {
        if (error instanceof EipaError) throw error

        const retryable = error instanceof RetryableResponse
        if (!retryable && !isTransportError(error)) throw error

        lastStatus = retryable ? error.status : undefined
        lastCause = error
        if (attempt === retry.maxAttempts) break

        const advised = retryable ? error.retryAfterMs : undefined
        const delayMs = advised ?? eipaBackoffDelayMs(attempt, retry, random)
        await sleep(Math.min(delayMs, retry.maxDelayMs))
      }
    }

    const reason = lastStatus === undefined ? 'the request kept failing' : `status ${lastStatus}`
    throw new EipaRetryLimitError(
      `${EIPA_FILES[file]}: gave up after ${retry.maxAttempts} attempts (${reason}). ` +
        `URL ${url}.`,
      { file, url, attempts: retry.maxAttempts, lastStatus, cause: lastCause },
    )
  }

  /** Body-or-304, re-requesting unconditionally when the caller needs the data. */
  const read = async (file: EipaFileName, requireBody: boolean): Promise<HttpResult> => {
    const url = urlFor(file)
    const first = await request(file, url, true)
    if (first.status !== 304 || first.text !== undefined) return first
    if (!requireBody) return first
    // 304 with nothing cached to serve: the store keeps validators only. Spend a
    // second request rather than hand the pipeline a hole.
    return request(file, url, false)
  }

  const parsePayload = (file: EipaFileName, text: string): ParsedFile<unknown> => {
    let payload: unknown
    try {
      payload = JSON.parse(text) as unknown
    } catch (error) {
      throw new EipaValidationError(file, [
        `${EIPA_FILES[file]}: <root>: not valid JSON (${
          error instanceof Error ? error.message : String(error)
        })`,
      ])
    }

    if (file === 'dictionary') return parseDictionaryFile(payload)
    if (file === 'operator')
      return parseRowFile(file, EipaOperator, rowIdBy('id'), payload, rowErrorMode)
    if (file === 'pool') return parseRowFile(file, EipaPool, rowIdBy('id'), payload, rowErrorMode)
    if (file === 'station')
      return parseRowFile(file, EipaStation, rowIdBy('id'), payload, rowErrorMode)
    if (file === 'point') return parseRowFile(file, EipaPoint, rowIdBy('id'), payload, rowErrorMode)
    return parseRowFile(file, EipaDynamic, rowIdBy('point_id'), payload, rowErrorMode)
  }

  const load = async <T>(file: EipaFileName): Promise<T> => {
    const result = await read(file, true)
    const text = result.text ?? ''
    return parsePayload(file, text).value as T
  }

  const fetchRawFile = async (file: EipaFileName): Promise<EipaRawFile> => {
    const url = urlFor(file)
    const result = await read(file, false)
    if (result.text === undefined)
      return {
        file,
        url,
        notModified: true,
        etag: result.etag,
        lastModified: result.lastModified,
      }

    const parsed = parsePayload(file, result.text)
    return {
      file,
      url,
      notModified: false,
      text: result.text,
      generated: parsed.generated,
      rowCount: parsed.rowCount,
      rowErrors: parsed.rowErrors,
      etag: result.etag,
      lastModified: result.lastModified,
    }
  }

  const fetchDictionary = (): Promise<EipaDictionaryFile> => load<EipaDictionaryFile>('dictionary')
  const fetchOperators = (): Promise<EipaOperatorFile> => load<EipaOperatorFile>('operator')
  const fetchPools = (): Promise<EipaPoolFile> => load<EipaPoolFile>('pool')
  const fetchStations = (): Promise<EipaStationFile> => load<EipaStationFile>('station')
  const fetchPoints = (): Promise<EipaPointFile> => load<EipaPointFile>('point')
  const fetchDynamic = (): Promise<EipaDynamicFile> => load<EipaDynamicFile>('dynamic')

  /** Sequential on purpose: six parallel downloads is how an account gets throttled. */
  const fetchAll = async (): Promise<EipaSnapshot> => ({
    dictionary: await fetchDictionary(),
    operator: await fetchOperators(),
    pool: await fetchPools(),
    station: await fetchStations(),
    point: await fetchPoints(),
    dynamic: await fetchDynamic(),
  })

  return {
    fetchDictionary,
    fetchOperators,
    fetchPools,
    fetchStations,
    fetchPoints,
    fetchDynamic,
    fetchAll,
    fetchRawFile,
    budgets,
  }
}

/** Internal: a response worth another attempt. Never escapes the client. */
class RetryableResponse extends Error {
  readonly status: number
  readonly retryAfterMs?: number

  constructor(status: number, retryAfterMs?: number) {
    super(`EIPA answered ${status}`)
    this.name = 'RetryableResponse'
    this.status = status
    this.retryAfterMs = retryAfterMs
  }
}

/**
 * A `fetch` rejection - DNS, TLS, socket reset, timeout. Worth retrying; a
 * programming error (`TypeError: url is not a string`) is not, but the two are
 * indistinguishable from here, so the retry ceiling is what bounds the damage.
 */
const isTransportError = (error: unknown): boolean => error instanceof Error
